<template>
  <teleport to="head">
    <MetaTags
      :title="`DNS propagation for ${domain}`"
      :description="`${domain}'s global DNS propagation shown on a map. Shows authoritative responses, global DNS servers, local DNS servers, and more.`"
      :url="`https://www.nslookup.io/domains/${domainPunycode}/dns-propagation/${recordType.toLowerCase()}/`"
    />
  </teleport>
  <HeaderComponent :domain="domain" :domain-search-to-url="(encodedDomain) => `/domains/${encodedDomain}/dns-propagation/${recordType.toLowerCase()}/`" search-button-text="Check propagation" />
  <div class="flex-grow bg-[#f5f5f5]">
    <main class="container mx-auto px-4 pt-6 pb-14 sm:px-6 lg:px-8">
      <div class="flex flex-row flex-wrap justify-between mt-8 mb-6 gap-3 md:items-center">
        <div class="w-full lg:w-auto">
          <h1 class="text-3xl tracking-tight font-medium text-gray-900">DNS propagation for {{ domain === domainPunycode ? domain : `${domainPunycode} (${domain})`}}</h1>
          <router-link :to="`/domains/${domainPunycode}/dns-records/`" class="underline text-sm font-medium text-blue-600">All DNS records</router-link>
        </div>
        <RecordTypePicker v-model="recordType" />
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 clear-both">
        <div class="mb-6 col-span-2">
          <AdUnit class="float-right ml-6 shadow rounded-lg overflow-hidden" :config="PropagationTop" />
          <div class="prose">
            <p>We've queried 9 regional DNS servers, 9 global DNS networks, and {{ authoritativeResponses.size }} authoritative DNS servers for {{ domainPunycode }}'s {{ recordType }} records.</p>
            <p>Their responses are grouped, so you can easily see which DNS servers are returning the same records.</p>
            <template v-if="!loading && variants.length > 0">
              <template v-if="variants.length === 1">
                <template v-if="variants[0].records.length === 0">
                  <AlertComponent v-if="recordType === 'NS'" type="warning" class="flex not-prose shadow-sm" :has-border="true">None of the servers returned any NS records.<br />This means that the domain name has not been <router-link to="/learning/zone-delegation/" class="font-medium underline underline-offset-2">delegated</router-link>. If this domain was recently registered, then the <router-link to="/learning/how-does-domain-name-registration-work/#the-registry" class="font-medium underline underline-offset-2">registry</router-link> likely hasn't updated their DNS records yet.</AlertComponent>
                  <AlertComponent v-else type="info" class="flex not-prose shadow-sm" :has-border="true">None of the servers returned any records.<br />If this is not what you were expecting, then <router-link :to="`/domains/${domainPunycode}/dns-propagation/ns/`" class="font-medium underline underline-offset-2">check the NS records</router-link> for this domain to see if they have propagated.</AlertComponent>
                </template>
                <AlertComponent v-else type="success" class="flex not-prose shadow-sm" :has-border="true">All servers returned the same record set. This means that the DNS records have fully propagated.</AlertComponent>
              </template>
              <template v-else-if="['A', 'AAAA'].includes(recordType)">
                <AlertComponent v-if="variants.length > 2" type="info" class="flex not-prose shadow-sm" :has-border="true">There are more than two variants. The authoritative servers likely return different record sets in order to spread the load among multiple webservers.</AlertComponent>
              </template>
              <template v-else>
                <AlertComponent v-if="authoritativeServersDisagree" type="warning" class="flex not-prose shadow-sm" :has-border="true">The authoritative DNS servers returned different record sets. This indicates an issue in {{ domain }}'s DNS configuration.</AlertComponent>
                <AlertComponent v-else type="warning" class="flex not-prose shadow-sm" :has-border="true">{{ domain }}'s DNS records have not yet fully propagated.<template v-if="maxTtl"> Based on the response TTLs, this will likely persist for another <DurationComponent :seconds="maxTtl" />.</template></AlertComponent>
              </template>
            </template>
          </div>
        </div>
        <PropagationMap :markers="markers" class="rounded-lg shadow overflow-hidden" />
      </div>

      <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 clear-both">
        <li v-for="(variant, index) in variants" :key="index" class="flex flex-col col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow overflow-hidden ring-1 ring-black ring-opacity-5">
          <div class="px-4 py-5 sm:px-6 bg-gray-50">
            <div class="-ml-4 -mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="ml-4 mt-4">
                <div class="flex items-center space-x-3">
                  <h3 class="text-lg font-medium leading-6 text-gray-900">{{ variants.length === 1 ? 'DNS records' : `Variant ${getVariantName(index)}` }}</h3>
                  <span v-if="variant.isAuthoritative" class="inline-block flex-shrink-0 rounded-full bg-green-100 ml-2 px-2 py-0.5 text-xs font-medium text-green-800">Authoritative</span>
                </div>
                <p class="mt-1 text-sm text-gray-500">Served by {{ variants.length == 1 ? 'all ' : ''}}{{ variant.answers.length == 1 ? '1 server' : `${variant.answers.length} servers`}}.</p>
              </div>
              <div class="ml-4 mt-4 flex-shrink-0">
                <div class="flex -space-x-2">
                  <template v-if="variant.answers.length > 5">
                    <img v-for="(answer, index) in variant.answers.slice(0, 4)" :key="index" class="inline-block h-8 w-8 rounded-full ring-2 ring-white" :src="iconSrc(answer)" :alt="iconAlt(answer)" :title="iconAlt(answer)" />
                    <span class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500">
                      <span class="text-sm font-medium leading-none text-white">+{{ variant.answers.length - 4 }}</span>
                    </span>
                  </template>
                  <img v-else v-for="(answer, index) in variant.answers" :key="index" class="inline-block h-8 w-8 rounded-full ring-2 ring-white" :src="iconSrc(answer)" :alt="iconAlt(answer)" :title="iconAlt(answer)" />
                </div>
              </div>
            </div>
          </div>
          <pre v-if="variant.records.length" class="flex-grow font-mono overflow-x-auto px-4 py-5 sm:px-6">{{variant.records.map((rr) => rr.raw).join('\n')}}</pre>
          <div v-else class="flex-grow px-4 py-5 sm:px-6">No {{ recordType }} records found</div>
        </li>
      </ul>

      <AdUnit class="mt-8 mx-auto" :config="PropagationMid" />

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <div class="col-span-1">
          <h2 class="pt-12 text-xl font-semibold text-gray-900">Authoritative DNS servers</h2>
          <p class="mt-2 pb-3 text-sm text-gray-700">{{ formattedQueryLocation ? `Queried from ${formattedQueryLocation}` : '&nbsp;' }}</p>
          <PropagationServerList :items="getAuthoritativeResponses()" />
        </div>
        <div class="col-span-1">
          <h2 class="pt-12 text-xl font-semibold text-gray-900">Global DNS networks</h2>
          <p class="mt-2 pb-3 text-sm text-gray-700">{{ formattedQueryLocation ? `Queried from ${formattedQueryLocation}` : '&nbsp;' }}</p>
          <PropagationServerList :items="organizationList.map((queryTarget) => getGlobalResponse(queryTarget))" />
        </div>
        <div class="col-span-1 lg:col-span-2 xl:col-span-1">
          <h2 class="pt-12 text-xl font-semibold text-gray-900">Regional DNS servers</h2>
          <p class="mt-2 pb-3 text-sm text-gray-700">These servers may cache DNS records.</p>
          <PropagationServerList class="min-w-full" :items="countryList.map((queryTarget) => getCountryResponse(queryTarget))" />
        </div>
      </div>

      <AdUnit class="mt-8 mx-auto" :config="PropagationBottom" />

    </main>
  </div>
  <FooterComponent />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import router from '@/router';
import {
  addResponse, getVariant, getVariantName, Variant,
} from '@/common/propagationVariant';
import {
  flagIconSrc, organizationIconSrc, displayHostingEntity, iconSrc, iconAlt,
} from '@/common/hostingEntity';
import MetaTags from '@/components/MetaTags.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import RecordTypePicker from '@/components/RecordTypePicker.vue';
import PropagationServerList, { PropagationServerListItem } from '@/components/dns-propagation/PropagationServerList.vue';
import {
  DnsPropagationAnswer, DnsPropagationResult, GlobalQueryTarget, isGlobalQueryTarget, RegionalQueryTarget,
} from '@/model/PropagationEndpoint';
import PropagationMap from '@/components/dns-propagation/PropagationMap.vue';
import AlertComponent from '@/components/micro/AlertComponent.vue';
import DurationComponent from '@/components/micro/DurationComponent.vue';
import { DnsServer } from '@/model/Api';
import AdUnit from '@/components/ads/AdUnit.vue';
import {
  Billboard, LargeLeaderboard, LargeRectangle, Leaderboard, MediumRectangle,
} from '@/common/adSizes';
import { PropagationTop, PropagationMid, PropagationBottom } from '@/common/adSlots';

const props = defineProps({
  routerDomain: {
    type: String,
    required: true,
  },
  routerRecordType: {
    type: String,
    required: true,
  },
});

const organizationList: Array<GlobalQueryTarget> = ['CloudFlare', 'Google', 'Quad9', 'OpenDns', 'CleanBrowsing', 'AdGuard', 'NextDNS', 'ControlDUnfiltered', 'UltimateHostsBlacklist'];
const countryList: Array<RegionalQueryTarget> = ['SouthAfrica', 'TheNetherlands', 'Australia', 'India', 'USA', 'Brazil', 'Ukraine', 'Russia', 'Canada'];

const domain = ref('');
const domainPunycode = ref('');
const loading = ref(true);

const recordType = ref(props.routerRecordType.toUpperCase());

const globalResponses = ref<Map<GlobalQueryTarget, DnsPropagationAnswer>>(new Map());
const regionalResponses = ref<Map<RegionalQueryTarget, DnsPropagationAnswer>>(new Map());
const authoritativeResponses = ref<Map<DnsServer, DnsPropagationAnswer>>(new Map());
const variants = ref<Array<Variant>>([]);

const formattedQueryLocation = computed(() => {
  const location = [...globalResponses.value.values(), ...regionalResponses.value.values(), ...authoritativeResponses.value.values()][0]?.queryLocation;
  if (location === undefined) {
    return undefined;
  }
  if (location === 'Amsterdam') {
    return 'Amsterdam, the Netherlands';
  }
  return 'San Fransisco, USA';
});
const authoritativeServersDisagree = computed(() => variants.value.filter((variant) => variant.isAuthoritative).length > 1);
const maxTtl = computed(() => Math.max(...variants.value.filter((variant) => !variant.isAuthoritative).flatMap((variant) => variant.records.map((rr) => rr.ttl))));
const markers = computed(() => [...regionalResponses.value.values()].flatMap((response) => {
  if (response.server.location) {
    return [{
      lat: response.server.location.lat,
      lon: response.server.location.lon,
      variant: variants.value.length === 1 || getVariant(variants.value, response),
    }];
  }
  return [];
}));

function load() {
  loading.value = true;
  globalResponses.value = new Map();
  regionalResponses.value = new Map();
  authoritativeResponses.value = new Map();
  variants.value = [];

  const connection = new WebSocket(`${window.location.origin.replace(/^http/, 'ws')}/api/v1/ws/dns-propagation`);

  connection.onmessage = (event) => {
    const response: DnsPropagationResult = JSON.parse(event.data);
    if ('Left' in response) {
      // eslint-disable-next-line
      console.error(response.Left);
    } else {
      const answer = response.Right;
      if (answer.queryTarget === 'Authoritative') {
        authoritativeResponses.value.set(answer.server, answer);
      } else if (isGlobalQueryTarget(answer.queryTarget)) {
        globalResponses.value.set(answer.queryTarget, answer);
      } else {
        regionalResponses.value.set(answer.queryTarget, answer);
      }
      variants.value = addResponse(variants.value, answer, recordType.value);
    }
  };

  connection.onopen = () => {
    connection.send(JSON.stringify({ domain: props.routerDomain, recordType: props.routerRecordType }));
  };

  connection.onclose = () => {
    loading.value = false;
  };
}

function getGlobalResponse(qt: GlobalQueryTarget): PropagationServerListItem {
  const response = globalResponses.value.get(qt);
  if (response) {
    return {
      firstLine: displayHostingEntity(qt),
      secondLine: response.server.ip || '',
      ttl: Math.max(...response.answer.map((rr) => rr.ttl)),
      variant: variants.value.length === 1 || getVariant(variants.value, response),
      iconSrc: iconSrc(response),
      iconAlt: iconAlt(response),
      iconShape: 'circle',
    };
  }
  return {
    firstLine: displayHostingEntity(qt),
    secondLine: loading.value ? 'loading' : 'DNS server did not reply',
    ttl: null,
    variant: null,
    iconSrc: organizationIconSrc(qt),
    iconAlt: displayHostingEntity(qt),
    iconShape: 'circle',
  };
}

function getCountryResponse(qt: RegionalQueryTarget): PropagationServerListItem {
  const response = regionalResponses.value.get(qt);
  if (response) {
    return {
      firstLine: displayHostingEntity(qt),
      secondLine: response.server.ip || '',
      ttl: Math.max(...response.answer.map((rr) => rr.ttl)),
      variant: variants.value.length === 1 || getVariant(variants.value, response),
      iconSrc: flagIconSrc(qt),
      iconAlt: iconAlt(response),
      iconShape: '4x3',
    };
  }
  return {
    firstLine: displayHostingEntity(qt),
    secondLine: loading.value ? 'Loading...' : 'DNS server did not reply.',
    ttl: null,
    variant: null,
    iconSrc: flagIconSrc(qt),
    iconAlt: displayHostingEntity(qt),
    iconShape: '4x3',
  };
}

function getAuthoritativeResponses(): PropagationServerListItem[] {
  function sortFn(l: PropagationServerListItem, r: PropagationServerListItem) {
    if (l.firstLine > r.firstLine) {
      return 1;
    }
    if (l.firstLine < r.firstLine) {
      return -1;
    }
    return 0;
  }
  return Array.from(authoritativeResponses.value).map(([server, answer]) => ({
    firstLine: server.domain || '',
    secondLine: server.ip || '',
    ttl: Math.max(...answer.answer.map((rr) => rr.ttl)),
    variant: variants.value.length === 1 || getVariant(variants.value, answer),
    iconSrc: iconSrc(answer),
    iconAlt: iconAlt(answer),
    iconShape: 'circle' as const,
  })).sort(sortFn);
}

watch(() => recordType.value, () => {
  router.push(`/domains/${domainPunycode.value}/dns-propagation/${recordType.value.toLowerCase()}/`);
});

watch(() => props.routerDomain, () => {
  if (domainPunycode.value !== props.routerDomain) {
    domain.value = decodeURI(props.routerDomain)
      .toLowerCase();
    domainPunycode.value = decodeURI(props.routerDomain)
      .toLowerCase();
    load();
  }
}, {
  immediate: true,
});

watch(() => props.routerRecordType, () => {
  recordType.value = props.routerRecordType.toUpperCase();
  load();
});

</script>
